import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Bell } from 'lucide-react';

import { Button } from '@/shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import {
  useMarkNotificationsRead,
  useNotifications,
  type AppNotification,
} from '@/shared/hooks/use-notifications';

const DATETIME_FMT = new Intl.DateTimeFormat('ro-RO', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Renders the notification text from `type` + `data` through i18n. Unknown
 * types (e.g. rows created by a newer backend) fall back to the raw type.
 */
function notificationText(
  n: AppNotification,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const key = `notifications.types.${n.type}`;
  // `count` drives i18next plural forms ("1 intrare" / "2 intrări" / "20 de intrări").
  const text = t(key, { ...n.data, count: n.data.collection_count, defaultValue: '' });
  return text || n.type;
}

export function NotificationsBell(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const query = useNotifications();
  const markRead = useMarkNotificationsRead();

  const unread = query.data?.meta.unread_count ?? 0;
  const items = query.data?.data ?? [];

  const handleItemClick = (n: AppNotification): void => {
    if (!n.read_at) markRead.mutate([n.id]);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={t('notifications.ariaLabel', { count: unread })}
        >
          <Bell className="size-4" />
          {unread > 0 ? (
            <span
              key={unread}
              className="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-destructive text-xs font-semibold leading-none text-destructive-foreground motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-50"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-semibold">{t('notifications.title')}</span>
          {unread > 0 ? (
            <Button
              variant="ghost"
              disabled={markRead.isPending}
              onClick={() => markRead.mutate(undefined)}
            >
              {t('notifications.markAllRead')}
            </Button>
          ) : null}
        </div>
        <div className="max-h-[min(400px,60dvh)] overflow-y-auto">
          {query.isPending ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t('common.loading')}
            </p>
          ) : items.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t('notifications.empty')}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleItemClick(n)}
                    className="flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors hover:bg-muted/40"
                  >
                    <span
                      className={`mt-1.5 size-2 shrink-0 rounded-full ${n.read_at ? 'bg-transparent' : 'bg-primary'}`}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 space-y-0.5">
                      <span className={`block text-sm leading-snug ${n.read_at ? 'text-muted-foreground' : 'font-medium'}`}>
                        {notificationText(n, t)}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {DATETIME_FMT.format(new Date(n.created_at))}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
